import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { getStoredUTMs, trackServiceRequestSubmit, trackFormStarted, trackFileUpload } from '../lib/analytics';

const MAX_PHOTOS = 5;
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "403-615-8770";
const EMAIL = "Radinovicw@shaw.ca";

const serviceOptions = [
  'Plumbing - General',
  'Plumbing - Repair / Leak',
  'Plumbing - Tankless Water Heater',
  'Plumbing - Hot Water Tank',
  'Plumbing - Poly-B Replacement',
  'Plumbing - Drain Cleaning',
  'Plumbing - Bathroom / Kitchen',
  'Plumbing - Secondary Suite / Basement',
  'Heating - Furnace Repair',
  'Heating - Furnace Installation',
  'Heating - Boiler / Hydronic',
  'Heating - In-Floor / Radiant',
  'Heating - Garage Heater',
  'Heating - Thermostat / Controls',
  'Gas Fitting - Gas Line Installation',
  'Gas Fitting - Gas Safety & Testing',
  'Gas Fitting - BBQ Line',
  'Gas Fitting - Appliance Hookup',
  'Gas Fitting - Gas Fireplace',
  'Backflow / Cross Connection Testing',
  'Renovation / Light Commercial',
  'Other'
];

export const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestType = searchParams.get('type') || 'service';

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    service_needed: '',
    description: '',
    preferred_time: '',
    request_type: requestType === 'video' ? 'video' : 'service'
  });

  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      request_type: requestType === 'video' ? 'video' : 'service'
    }));
  }, [requestType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      e.target.value = '';
      return;
    }

    const accepted = [];
    for (const file of files) {
      // HEIC/HEIF often report empty content_type on some browsers — allow by extension too
      const nameLower = (file.name || '').toLowerCase();
      const extOk = /\.(jpe?g|png|webp|heic|heif)$/i.test(nameLower);
      const typeOk = ALLOWED_PHOTO_MIME_TYPES.includes((file.type || '').toLowerCase());
      if (!typeOk && !extOk) {
        toast.error(`${file.name}: unsupported format (JPEG, PNG, WebP, HEIC, HEIF only)`);
        continue;
      }
      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        toast.error(`${file.name}: exceeds 5 MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) {
      e.target.value = '';
      return;
    }

    // Fire GA4 file_upload event (once per selection batch)
    trackFileUpload({ count: accepted.length, form_name: 'service_request' });

    // Store real File objects with a preview URL for the thumbnail
    accepted.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos((prev) => [...prev, { file, name: file.name, preview: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });
    // Reset the input so selecting the same file again re-triggers change
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Build multipart form data (backend now accepts multipart on /api/service-requests)
      const utm = getStoredUTMs();
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
      });
      Object.entries(utm).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      photos.forEach((p) => {
        if (p.file) fd.append('photos', p.file, p.name);
      });

      await axios.post(`${API}/service-requests`, fd);

      // Fire Google Ads form conversion + GA4 success event (only after API success)
      trackServiceRequestSubmit({
        service_needed: formData.service_needed,
        request_type: formData.request_type,
      });

      // Redirect to thank you page on success
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'There was an error submitting your request. Please try again or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="page-transition">
      <Helmet>
        <title>Contact DNB Plumbing | Calgary Plumber | 403-615-8770</title>
        <meta name="description" content="Contact DNB Plumbing, Heating & Gas Fitting Inc. in Calgary. Call Mike at 403-615-8770 or email Radinovicw@shaw.ca for plumbing, heating, and gas fitting service." />
        <link rel="canonical" href="/contact" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-white"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="eyebrow mb-5">Get in Touch</span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] mt-5 mb-6 leading-tight">
              Book your <span className="text-[#0284C7]">service</span>.
            </h1>
            <p className="text-slate-600 text-lg">
              Fill out the form below and Mike will get back to you promptly,
              or call directly for immediate assistance.
            </p>

            {/* Pricing Info */}
            <div className="mt-8 dnb-card-soft max-w-md mx-auto text-left">
              <div className="flex items-center gap-3 mb-3">
                <div className="droplet-icon droplet-icon-sm droplet-icon-light">
                  <Clock className="w-5 h-5" />
                </div>
                <span className="font-heading font-bold text-[#0F172A]">Fast Service Available</span>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Same-day service available for many plumbing and mechanical issues.
              </p>
              <div className="text-xs text-slate-500 border-t border-slate-200 pt-3">
                Residential &middot; Light Commercial &middot; Service &amp; Renovation
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1 space-y-6"
            >
              <div>
                <h3 className="font-heading font-extrabold text-2xl text-[#0F172A] mb-6">
                  Contact Information
                </h3>
                
                <div className="space-y-4">
                  <a
                    href={`tel:${PHONE}`}
                    data-testid="contact-phone"
                    className="dnb-card-soft flex items-start gap-4 group"
                  >
                    <div className="droplet-icon droplet-icon-sm droplet-icon-light flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-slate-500 text-sm mb-1">Call Mike Direct</span>
                      <span className="text-[#0F172A] font-heading font-bold text-lg group-hover:text-[#0284C7] transition-colors">{PHONE}</span>
                    </div>
                  </a>

                  <a
                    href={`mailto:${EMAIL}`}
                    data-testid="contact-email"
                    className="dnb-card-soft flex items-start gap-4 group"
                  >
                    <div className="droplet-icon droplet-icon-sm droplet-icon-light flex-shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <span className="block text-slate-500 text-sm mb-1">Email Us</span>
                      <span className="text-[#0F172A] font-semibold break-all group-hover:text-[#0284C7] transition-colors">{EMAIL}</span>
                    </div>
                  </a>

                  <div className="dnb-card-soft flex items-start gap-4">
                    <div className="droplet-icon droplet-icon-sm droplet-icon-light flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-slate-500 text-sm mb-1">Service Area</span>
                      <span className="text-[#0F172A] font-semibold">Calgary &amp; Surrounding Areas</span>
                    </div>
                  </div>

                  <div className="dnb-card-soft flex items-start gap-4">
                    <div className="droplet-icon droplet-icon-sm droplet-icon-light flex-shrink-0">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-slate-500 text-sm mb-1">Business Hours</span>
                      <span className="text-[#0F172A] block font-semibold">Open 7 Days a Week</span>
                      <span className="text-[#0F172A] block">9:00 AM &ndash; 5:00 PM</span>
                      <span className="text-[#0284C7] text-sm font-bold">24 Hour Emergency Service Available</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <form
                onSubmit={handleSubmit}
                onFocusCapture={() => trackFormStarted({ form_name: 'service_request' })}
                onChangeCapture={() => trackFormStarted({ form_name: 'service_request' })}
                className="bg-slate-50 p-6 sm:p-8 rounded-lg border border-slate-200 shadow-xl shadow-slate-200/40"
              >
                <h3 className="font-heading font-extrabold text-2xl text-[#0F172A] mb-8">
                  Service Request Form
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-[#0F172A] text-sm font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="form-name"
                      className="form-input w-full p-4"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-[#0F172A] text-sm font-semibold mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="form-phone"
                      className="form-input w-full p-4"
                      placeholder="(403) 555-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-[#0F172A] text-sm font-semibold mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="form-email"
                      className="form-input w-full p-4"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-[#0F172A] text-sm font-semibold mb-2">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      data-testid="form-address"
                      className="form-input w-full p-4"
                      placeholder="Service address"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-[#0F172A] text-sm font-semibold mb-2">Service Needed *</label>
                  <select
                    name="service_needed"
                    value={formData.service_needed}
                    onChange={handleChange}
                    required
                    data-testid="form-service"
                    className="form-input w-full p-4 bg-white"
                  >
                    <option value="">Select a service...</option>
                    {serviceOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-[#0F172A] text-sm font-semibold mb-2">Description of Problem</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    data-testid="form-description"
                    className="form-input w-full p-4 resize-none"
                    placeholder="Please describe the issue or service you need..."
                  />
                </div>

                {/* Photo Upload */}
                <div className="mb-6">
                  <label className="block text-[#0F172A] text-sm font-semibold mb-2">
                    Upload Photos (Optional)
                  </label>
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-[#0284C7] transition-colors bg-white/60">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-[#0284C7] mx-auto mb-2" />
                      <p className="text-[#0F172A] text-sm font-semibold">Click to upload photos of the issue</p>
                      <p className="text-slate-500 text-xs mt-1">Max 5 photos</p>
                    </label>
                  </div>
                  
                  {/* Photo Previews */}
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={photo.preview} 
                            alt={photo.name}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-8">
                  <label className="block text-[#0F172A] text-sm font-semibold mb-2">Preferred Time</label>
                  <input
                    type="text"
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleChange}
                    data-testid="form-time"
                    className="form-input w-full p-4"
                    placeholder="e.g., Weekday mornings, ASAP, etc."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="form-submit"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Service Request
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="py-16 section-tint">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="eyebrow mb-4">Financing</span>
          <h2 className="font-heading font-extrabold text-2xl text-[#0F172A] mt-4 mb-3">
            Financing Options Available
          </h2>
          <p className="text-slate-600 mb-5">
            For larger repairs and installations, we offer financing options to make the work more manageable.
            Ask about available financing when you call.
          </p>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center gap-2 text-[#0284C7] font-bold hover:gap-3 transition-all"
          >
            <Phone className="w-5 h-5" />
            Call to Learn More: {PHONE}
          </a>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
